import { ActionModal, FormError, FormLayout, PageLayout } from '@kcftech/design-system';
import { ISystemOverviewResponse } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useCurrentUserInfo } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { useRunningAlertThresholdJobs } from 'custom_hooks/data_hooks/jobs/useRunningAlertThresholdJobs';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { RequiredMessage } from 'shared/form/RequiredMessage';
import { extractErrorString } from 'utils/api';
import { getSystemWorkbenchTab, WorkbenchTab } from 'utils/RouteHelper';
import { sentryCaptureException } from 'utils/sentry';
import { AlarmCard } from './AlarmCard';
import { BoundsCard } from './BoundsCard';
import { LocationSelectionField } from './LocationSelectionField';
import { NotificationCard } from './NotificationCard';
import { RunningThresholdJobsWarningCard } from './RunningThresholdJobsWarningCard';
import { SamplingCard } from './SamplingCard';
import {
    AlarmThresholdFormFields,
    buildAlertThresholdRequest,
    getDefaultIndicatorTypeConfigs,
} from './shared';
import {
    AlarmAndNotificationWrapper,
    FlushPrimary,
    StyledCheckbox,
    StyledLink,
    StyledSecondary,
} from './styled';

export type GenerateAlarmThresholdsModalProps = Readonly<{
    locationId: string;
    system?: ISystemOverviewResponse;
    onClose: () => void;
}>;

export function GenerateAlarmThresholdsModal({
    locationId,
    system,
    onClose,
}: GenerateAlarmThresholdsModalProps) {
    const { showToast } = useNotificationContext();

    const { data: currentUser } = useCurrentUserInfo();

    const { runningThresholdJobs, runningThresholdJobsError } =
        useRunningAlertThresholdJobs(locationId);

    const methods = useForm<AlarmThresholdFormFields>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        defaultValues: {
            start: null,
            end: null,
            warning: 70,
            criticalAlarm: 150,
            onlyToNotAlreadySetIndicators: false,
            groupIds: [],
            indicatorTypes: getDefaultIndicatorTypeConfigs(currentUser?.Profile),
        },
    });

    const {
        handleSubmit,
        formState: { errors },
        control,
    } = methods;

    const { mutate: generateAlarms, isPending: isGeneratingAlarms } = useMutation({
        mutationFn: async (data: AlarmThresholdFormFields) =>
            sdClient.runAlertThresholdJob({
                body: buildAlertThresholdRequest(data, locationId, currentUser?.Profile),
            }),
        onSuccess: () => {
            showToast({
                type: 'success',
                content: alarmSettingsRoute ? (
                    <>
                        <div className="mb-5">
                            Alarm thresholds generating successfully
                        </div>
                        <StyledLink to={alarmSettingsRoute}>
                            See Alarm Settings
                        </StyledLink>
                    </>
                ) : (
                    'Alarm thresholds generating successfully'
                ),
            });
            onClose();
        },
        onError: (error) => {
            sentryCaptureException({
                error,
                tags: { component: 'GenerateAlarmThresholdsModal' },
                context: { mutationError: { locationId } },
            });

            const errorMessage = extractErrorString(error) ?? 'Unknown error occurred';
            showToast({
                type: 'error',
                content: `Failed to generate alarm thresholds: ${errorMessage}`,
            });
        },
    });

    const alarmSettingsRoute = system
        ? `${getSystemWorkbenchTab(system.Id, WorkbenchTab.Indicators)}/alarmSettings`
        : undefined;

    const onSave = (data: AlarmThresholdFormFields) => {
        generateAlarms(data);
    };

    return (
        <ActionModal
            title="Generate Alarm Thresholds"
            width={1172}
            height={842}
            actionButtonText="Save"
            closeButtonText="Close"
            actionable={!isGeneratingAlarms}
            onAction={handleSubmit(onSave)}
            onClose={() => onClose()}
        >
            <PageLayout layout="task">
                <FormProvider {...methods}>
                    <FlushPrimary className="pb-50">
                        <FormLayout>
                            <span className="mb-10">
                                Automatically generate alarm thresholds using historical
                                sensor data by configuring these fields to establish the
                                baseline.
                            </span>
                            <RunningThresholdJobsWarningCard
                                runningThresholdJobs={runningThresholdJobs}
                                runningThresholdJobsError={runningThresholdJobsError}
                            />
                            <RequiredMessage />
                            <SamplingCard />
                            <BoundsCard profile={currentUser?.Profile} />
                            <AlarmAndNotificationWrapper>
                                <AlarmCard />
                                <NotificationCard />
                            </AlarmAndNotificationWrapper>
                            <Controller
                                name="onlyToNotAlreadySetIndicators"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <StyledCheckbox
                                        checked={value}
                                        onChange={onChange}
                                        label="Only apply to indicators without thresholds"
                                    />
                                )}
                            />
                        </FormLayout>
                    </FlushPrimary>
                    <StyledSecondary>
                        {errors.groupIds?.message && (
                            <FormError message={errors.groupIds.message} />
                        )}
                        <LocationSelectionField
                            hideIndicatorGroups
                            rootGroupIds={system ? [system.Id] : undefined}
                        />
                    </StyledSecondary>
                </FormProvider>
            </PageLayout>
        </ActionModal>
    );
}
